import React from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* =========================================================
   CONFIGURATION
   ========================================================= */

const BASE_URL = "https://leg-up.epu.ntua.gr";

const DEFAULT_TITLE = "LEG-UP Platform";

const OG_IMAGE = `${BASE_URL}/logo2.png`;

const LOCALES = {
  en: "en_GB",
  es: "es_ES",
  ca: "ca_ES",
  nl: "nl_BE",
};

/* =========================================================
   MULTILINGUAL SEO CONTENT
   ========================================================= */

const SEO = {
  /* =======================================================
     ENGLISH
     ======================================================= */

  en: {
    "/": [
      "LEG-UP | Home Purchase & Energy Renovation Financing",
      "LEG-UP helps homebuyers with property purchase financing, energy-efficient renovations, and collaboration with lenders and One-Stop Shops in Belgium and Spain.",
    ],

    "/faq": [
      "Frequently Asked Questions | LEG-UP",
      "Find answers to common questions about the LEG-UP platform, financing, renovation planning and services.",
    ],

    "/contact": [
      "Contact Us | LEG-UP",
      "Contact the LEG-UP project team for information about the platform, its services and pilot activities.",
    ],

    "/privacy-policy": [
      "Privacy Policy | LEG-UP",
      "Read the LEG-UP privacy policy to understand how personal information is collected, processed and protected.",
    ],

    "/terms-and-conditions": [
      "Terms and Conditions | LEG-UP",
      "Read the terms and conditions governing the use of the LEG-UP digital platform.",
    ],

    "/services": [
      "Homebuyer Services | LEG-UP",
      "Explore LEG-UP services for home purchase financing, renovation planning and energy-efficiency improvements.",
    ],

    "/simulations": [
      "Loan & Renovation Simulations | LEG-UP",
      "Explore LEG-UP simulation tools for financing, monthly instalments and estimated home renovation costs.",
    ],

    "/register/homebuyer": [
      "Homebuyer Registration | LEG-UP",
      "Register as a homebuyer to access LEG-UP home purchase financing and renovation support services.",
    ],

    "/register/lender": [
      "Lender Registration | LEG-UP",
      "Learn how lending organisations can register and participate in the LEG-UP platform.",
    ],

    "/register/oss/step-1": [
      "One-Stop Shop Registration | LEG-UP",
      "Register your One-Stop Shop organisation on the LEG-UP platform to support homebuyers with energy-efficient renovation planning, project coordination and financing services.",
    ],
  },

  /* =======================================================
     SPANISH
     ======================================================= */

  es: {
    "/": [
      "LEG-UP | Financiación de vivienda y rehabilitación energética",
      "LEG-UP ayuda a los compradores de vivienda con la financiación de la compra, la rehabilitación energética y la colaboración con entidades financieras y ventanillas únicas.",
    ],

    "/faq": [
      "Preguntas frecuentes | LEG-UP",
      "Encuentra respuestas a preguntas frecuentes sobre la plataforma LEG-UP, financiación, rehabilitación y servicios.",
    ],

    "/contact": [
      "Contacto | LEG-UP",
      "Contacta con el equipo del proyecto LEG-UP para obtener información sobre la plataforma y sus servicios.",
    ],

    "/privacy-policy": [
      "Política de privacidad | LEG-UP",
      "Consulta cómo LEG-UP recopila, trata y protege los datos personales.",
    ],

    "/terms-and-conditions": [
      "Términos y condiciones | LEG-UP",
      "Consulta los términos y condiciones de uso de la plataforma digital LEG-UP.",
    ],

    "/services": [
      "Servicios para compradores de vivienda | LEG-UP",
      "Descubre los servicios de LEG-UP para la financiación de vivienda y la rehabilitación energética.",
    ],

    "/simulations": [
      "Simulaciones de financiación y rehabilitación | LEG-UP",
      "Explora las herramientas de simulación de LEG-UP para préstamos, cuotas mensuales y costes estimados de rehabilitación.",
    ],

    "/register/homebuyer": [
      "Registro de compradores de vivienda | LEG-UP",
      "Regístrate como comprador de vivienda para acceder a los servicios de financiación y rehabilitación de LEG-UP.",
    ],

    "/register/lender": [
      "Registro de entidades financieras | LEG-UP",
      "Descubre cómo registrar una entidad financiera y participar en la plataforma LEG-UP.",
    ],

    "/register/oss/step-1": [
      "Registro de Ventanillas Únicas | LEG-UP",
      "Registra tu organización de Ventanilla Única en LEG-UP para ayudar a los compradores de vivienda con la planificación de rehabilitaciones energéticas y la coordinación de proyectos.",
    ],
  },

  /* =======================================================
     CATALAN
     ======================================================= */

  ca: {
    "/": [
      "LEG-UP | Finançament d'habitatges i rehabilitació energètica",
      "LEG-UP ajuda els compradors d'habitatges amb el finançament de la compra, la rehabilitació energètica i la col·laboració amb entitats financeres i finestretes úniques.",
    ],

    "/faq": [
      "Preguntes freqüents | LEG-UP",
      "Troba respostes a preguntes freqüents sobre la plataforma LEG-UP, el finançament, la rehabilitació i els serveis.",
    ],

    "/contact": [
      "Contacte | LEG-UP",
      "Contacta amb l'equip del projecte LEG-UP per obtenir informació sobre la plataforma i els seus serveis.",
    ],

    "/privacy-policy": [
      "Política de privacitat | LEG-UP",
      "Consulta com LEG-UP recull, tracta i protegeix les dades personals.",
    ],

    "/terms-and-conditions": [
      "Termes i condicions | LEG-UP",
      "Consulta els termes i condicions d'ús de la plataforma digital LEG-UP.",
    ],

    "/services": [
      "Serveis per a compradors d'habitatges | LEG-UP",
      "Descobreix els serveis de LEG-UP per al finançament d'habitatges i la rehabilitació energètica.",
    ],

    "/simulations": [
      "Simulacions de finançament i rehabilitació | LEG-UP",
      "Explora les eines de simulació de LEG-UP per a préstecs, quotes mensuals i costos estimats de rehabilitació.",
    ],

    "/register/homebuyer": [
      "Registre de compradors d'habitatges | LEG-UP",
      "Registra't com a comprador d'habitatge per accedir als serveis de finançament i rehabilitació de LEG-UP.",
    ],

    "/register/lender": [
      "Registre d'entitats financeres | LEG-UP",
      "Descobreix com registrar una entitat financera i participar a la plataforma LEG-UP.",
    ],

    "/register/oss/step-1": [
      "Registre de Finestretes Úniques | LEG-UP",
      "Registra la teva organització de Finestreta Única a LEG-UP per ajudar els compradors d'habitatges amb la planificació de rehabilitacions energètiques i la coordinació de projectes.",
    ],
  },

  /* =======================================================
     DUTCH
     ======================================================= */

  nl: {
    "/": [
      "LEG-UP | Financiering van woningaankoop en energierenovatie",
      "LEG-UP ondersteunt huizenkopers bij de financiering van hun woningaankoop, energiezuinige renovatie en samenwerking met kredietverstrekkers en One-Stop Shops.",
    ],

    "/faq": [
      "Veelgestelde vragen | LEG-UP",
      "Vind antwoorden op veelgestelde vragen over het LEG-UP-platform, financiering, renovatieplanning en diensten.",
    ],

    "/contact": [
      "Contact | LEG-UP",
      "Neem contact op met het LEG-UP-projectteam voor informatie over het platform en de beschikbare diensten.",
    ],

    "/privacy-policy": [
      "Privacybeleid | LEG-UP",
      "Lees hoe LEG-UP persoonsgegevens verzamelt, verwerkt en beschermt.",
    ],

    "/terms-and-conditions": [
      "Algemene voorwaarden | LEG-UP",
      "Lees de algemene voorwaarden voor het gebruik van het digitale LEG-UP-platform.",
    ],

    "/services": [
      "Diensten voor huizenkopers | LEG-UP",
      "Ontdek de diensten van LEG-UP voor woningfinanciering, renovatieplanning en energie-efficiëntie.",
    ],

    "/simulations": [
      "Financierings- en renovatiesimulaties | LEG-UP",
      "Ontdek de simulatietools van LEG-UP voor leningen, maandelijkse aflossingen en geraamde renovatiekosten.",
    ],

    "/register/homebuyer": [
      "Registratie voor huizenkopers | LEG-UP",
      "Registreer je als huizenkoper en krijg toegang tot financierings- en renovatiediensten van LEG-UP.",
    ],

    "/register/lender": [
      "Registratie voor kredietverstrekkers | LEG-UP",
      "Ontdek hoe kredietverstrekkers zich kunnen registreren en deelnemen aan het LEG-UP-platform.",
    ],

    "/register/oss/step-1": [
      "Registratie voor One-Stop Shops | LEG-UP",
      "Registreer uw One-Stop Shop-organisatie op het LEG-UP-platform om huizenkopers te ondersteunen bij energetische renovatieplanning, projectcoördinatie en financiering.",
    ],
  },
};

/* =========================================================
   META TAG HELPERS
   ========================================================= */

function setMeta(attribute, key, value) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", value);
}

function removeMeta(attribute, key) {
  document.head
    .querySelectorAll(`meta[${attribute}="${key}"]`)
    .forEach((element) => element.remove());
}

/* =========================================================
   CANONICAL URL
   ========================================================= */

function setCanonical(url) {
  const links = document.head.querySelectorAll('link[rel="canonical"]');

  if (!url) {
    links.forEach((link) => link.remove());
    return;
  }

  let element = links[0];

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.setAttribute("href", url);

  // Avoid duplicate canonical links.
  Array.from(links)
    .slice(1)
    .forEach((link) => link.remove());
}

/* =========================================================
   LOCALIZED SEO COMPONENT
   ========================================================= */

export default function LocalizedSEO() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  React.useEffect(() => {
    /* -----------------------------------------------------
       DETECT ACTIVE LANGUAGE
       ----------------------------------------------------- */

    const languageCode = (i18n.resolvedLanguage || i18n.language || "en")
      .split(/[-_]/)[0]
      .toLowerCase();

    const language = SEO[languageCode] ? languageCode : "en";

    document.documentElement.lang = language;

    /* -----------------------------------------------------
       NORMALIZE ROUTE
       ----------------------------------------------------- */

    const normalizedPath = pathname.replace(/\/+$/, "") || "/";

    const page = SEO[language][normalizedPath];

    /* -----------------------------------------------------
       NON-INDEXABLE ROUTES
       ----------------------------------------------------- */

    if (!page) {
      document.title = DEFAULT_TITLE;

      setMeta("name", "robots", "noindex, nofollow");

      removeMeta("name", "description");

      removeMeta("property", "og:title");
      removeMeta("property", "og:description");
      removeMeta("property", "og:url");
      removeMeta("property", "og:locale");

      removeMeta("name", "twitter:title");
      removeMeta("name", "twitter:description");

      setCanonical(null);

      return;
    }

    /* -----------------------------------------------------
       PAGE-SPECIFIC SEO
       ----------------------------------------------------- */

    const [title, description] = page;

    const canonicalUrl =
      normalizedPath === "/" ? `${BASE_URL}/` : `${BASE_URL}${normalizedPath}`;

    /* -----------------------------------------------------
       HTML DOCUMENT
       ----------------------------------------------------- */

    document.title = title;

    /* -----------------------------------------------------
       STANDARD SEO
       ----------------------------------------------------- */

    setMeta("name", "description", description);

    setMeta("name", "robots", "index, follow");

    setCanonical(canonicalUrl);

    /* -----------------------------------------------------
       OPEN GRAPH
       ----------------------------------------------------- */

    setMeta("property", "og:type", "website");

    setMeta("property", "og:site_name", "LEG-UP Platform");

    setMeta("property", "og:title", title);

    setMeta("property", "og:description", description);

    setMeta("property", "og:url", canonicalUrl);

    setMeta("property", "og:locale", LOCALES[language]);

    setMeta("property", "og:image", OG_IMAGE);

    setMeta("property", "og:image:alt", "LEG-UP Platform Logo");

    /* -----------------------------------------------------
       TWITTER / X
       ----------------------------------------------------- */

    setMeta("name", "twitter:card", "summary");

    setMeta("name", "twitter:title", title);

    setMeta("name", "twitter:description", description);

    setMeta("name", "twitter:image", OG_IMAGE);
  }, [pathname, i18n.language, i18n.resolvedLanguage]);

  return null;
}
